import { useLingui } from '@lingui/react/macro';
import { Image } from '@/routes/-components/image';
import { Belt } from '../belt';

function CoinArrowOut(props: { className?: string }) {
	return (
		<svg
			viewBox="0 0 32 32"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.5}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
			{...props}
		>
			<path d="M12.6667 18.3334C12.6667 19.6267 13.6668 20.6667 14.8934 20.6667H17.4001C18.4667 20.6667 19.3334 19.7601 19.3334 18.6267C19.3334 17.4134 18.8001 16.9734 18.0134 16.6934L14.0001 15.2934C13.2134 15.0134 12.6801 14.5867 12.6801 13.3601C12.6801 12.2401 13.5467 11.3201 14.6134 11.3201H17.1201C18.3467 11.3201 19.3468 12.3601 19.3468 13.6534" />
			<path d="M16 10V22" />
			<path d="M29.3334 16.0001C29.3334 23.3601 23.3601 29.3334 16.0001 29.3334C8.64008 29.3334 2.66675 23.3601 2.66675 16.0001C2.66675 8.64008 8.64008 2.66675 16.0001 2.66675" />
			<path d="M29.3333 8.00008V2.66675H24" />
			<path d="M22.6667 9.33341L29.3334 2.66675" />
		</svg>
	);
}

/**
 * `www` keeps its own list: change both, or the pages disagree about who we work with. White plates
 * are dropped with `mix-blend-multiply`, since the CDN keys on the path, not the contents. THIS DEPENDS
 * ON THE TILE STAYING LIGHT; on a dark one multiply would swallow the logos whole.
 */
const TECH_PARTNERS: { file: string; name: string }[] = [
	{ file: 'sabadell.png', name: 'Banco Sabadell' },
	{ file: 'paytef.png', name: 'PayTEF' },
	{ file: 'mastercard.png', name: 'Mastercard' },
	{ file: 'adyen.png', name: 'Adyen' },
	{ file: 'comercia-global-payments.png', name: 'Comercia Global Payments' },
	{ file: 'paycomet.svg', name: 'Paycomet' },
	{ file: 'wordline.png', name: 'Worldline' },
	{ file: 'infinitypay.png', name: 'InfinityPay' },
	{ file: 'necomplus.png', name: 'Necomplus' },
	{ file: 'sipay.png', name: 'Sipay' },
	{ file: 'toshiba.png', name: 'Toshiba' },
	{ file: 'diebold-nixford.png', name: 'Diebold Nixdorf' },
	{ file: 'marketpay-v2.svg', name: 'MarketPay' },
	{ file: 'ecopaynet.png', name: 'Ecopaynet' },
	{ file: 'saima-systems.png', name: 'Saima Systems' },
	{ file: 'sistemas-avanzados-en-monetica.png', name: 'Sistemas Avanzados en Monética' },
];

// Inline styles, as a Tailwind class that drifted from `TILE_GAP` would show a seam in the belt.
const TILE_W = 190;
const TILE_H = 96;
const TILE_GAP = 20;
/** The files are 125x55, and imgproxy never enlarges (`generateImagePath` passes no `enlarge`). */
const LOGO_BOX_W = TILE_W - 24;
const LOGO_H = 55;
const COLUMN_H = 560;
/** px per second. */
const SPEED = 26;

function LogoTile(props: { partner: (typeof TECH_PARTNERS)[number]; style: React.CSSProperties; boxW: number }) {
	return (
		<div style={props.style} className="bg-ink/5 isolate flex shrink-0 items-center justify-center rounded-lg px-3">
			{/* Sized HERE, as the shrink-to-fit `<picture>` would otherwise take the loaded bitmap's width. */}
			<div style={{ width: props.boxW, height: LOGO_H }} className="[&>picture]:contents">
				{props.partner.file.endsWith('.svg') ? (
					/* No raster derivative for a vector; `eager`, or it pops in mid-motion on the marquee. */
					<img
						src={`/home/tech-partners/${props.partner.file}`}
						alt={props.partner.name}
						loading="eager"
						fetchPriority="low"
						className="size-full object-contain mix-blend-multiply"
					/>
				) : (
					<Image
						src={`/home/tech-partners/${props.partner.file}`}
						alt={props.partner.name}
						height={LOGO_H}
						aspectRatio={props.boxW / LOGO_H}
						objectFit="contain"
						className="size-full object-contain! mix-blend-multiply"
					/>
				)}
			</div>
		</div>
	);
}

/** The per-tile bottom margin is what the loop needs. */
function LogoColumn(props: { logos: typeof TECH_PARTNERS; reverse?: boolean }) {
	return (
		<div
			style={{ height: COLUMN_H, width: TILE_W }}
			className="overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_18%,black_82%,transparent)]"
		>
			<Belt axis="y" speed={SPEED} reverse={props.reverse}>
				{props.logos.map((partner, i) => (
					<LogoTile
						// oxlint-disable-next-line react/no-array-index-key
						key={i}
						partner={partner}
						boxW={LOGO_BOX_W}
						style={{ height: TILE_H, marginBottom: TILE_GAP }}
					/>
				))}
			</Belt>
		</div>
	);
}

// Matches the merchant wheel's mobile rows, so both logo walls read as one motion.
const MOBILE_SPEED = 32;
const MOBILE_TILE_W = 150;
const MOBILE_TILE_H = 76;
const MOBILE_GAP = 12;

function LogoRowMobile(props: { logos: typeof TECH_PARTNERS; reverse?: boolean }) {
	return (
		<div className="overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
			<Belt speed={MOBILE_SPEED} reverse={props.reverse}>
				{props.logos.map((partner, i) => (
					<LogoTile
						// oxlint-disable-next-line react/no-array-index-key
						key={i}
						partner={partner}
						boxW={MOBILE_TILE_W - 24}
						style={{ width: MOBILE_TILE_W, height: MOBILE_TILE_H, marginRight: MOBILE_GAP }}
					/>
				))}
			</Belt>
		</div>
	);
}

export function TechPartners() {
	const { t } = useLingui();
	const half = Math.ceil(TECH_PARTNERS.length / 2);

	return (
		<section id="tech-partners" className="scroll-mt-24 overflow-hidden bg-white">
			<div className="mx-auto grid max-w-[1440px] items-center gap-12 px-6 py-16 md:py-20 lg:grid-cols-[minmax(0,1fr)_auto] xl:px-10">
				<div className="flex flex-col items-start gap-6">
					<div className="relative">
						<div
							aria-hidden
							className="bg-brand absolute inset-0 translate-x-1 translate-y-1 rotate-[10deg] rounded-lg"
						/>
						<div className="bg-ink relative grid size-16 place-items-center rounded-lg text-white">
							<CoinArrowOut className="size-8" />
						</div>
					</div>
					<div className="flex flex-col gap-2">
						<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
							{t`How we make it possible`}
						</h2>
						<p className="text-ink max-w-[533px] text-base leading-[1.5]">
							{t`We hold partnership agreements with the main payment gateways on the market and with technology partners, so the integration is native and secure.`}
						</p>
					</div>
				</div>

				<div className="hidden justify-center gap-5 xl:flex">
					<LogoColumn logos={TECH_PARTNERS.slice(0, half)} />
					<LogoColumn logos={TECH_PARTNERS.slice(half)} reverse />
				</div>

				{/* By index, so neither row holds all the wide wordmarks. */}
				<div className="flex flex-col gap-3 xl:hidden">
					<LogoRowMobile logos={TECH_PARTNERS.filter((_, i) => i % 2 === 0)} />
					<LogoRowMobile logos={TECH_PARTNERS.filter((_, i) => i % 2 === 1)} reverse />
				</div>
			</div>
		</section>
	);
}
